"use client";

import { useCallback, useEffect, useMemo, useState } from "react";
import type { AuthSession, LoginCredentials } from "@/types/auth";
import { ApiError } from "@/lib/api/client";
import { signIn } from "@/lib/api/auth";
import {
  clearAuthSessionCookie,
  hasAuthSession,
  readAuthSessionCookie,
  writeAuthSessionCookie,
} from "@/lib/auth/session";

export function getSignInErrorMessage(cause: unknown) {
  if (cause instanceof ApiError) {
    if (cause.status === 400 || cause.status === 401) {
      return "E-mail ou senha inválidos.";
    }
    if (cause.status === 403) {
      return "Seu acesso não está autorizado. Procure um administrador.";
    }
  }

  return "Não foi possível entrar. Tente novamente.";
}

export function useAuthSession() {
  const [session, setSessionState] = useState<AuthSession | null>(null);
  const [ready, setReady] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setSessionState(readAuthSessionCookie());
      setReady(true);
    }, 0);

    return () => window.clearTimeout(timer);
  }, []);

  const setSession = useCallback((nextSession: AuthSession) => {
    writeAuthSessionCookie(nextSession);
    setSessionState(nextSession);
    setError(null);
  }, []);

  const clearSession = useCallback(() => {
    clearAuthSessionCookie();
    setSessionState(null);
  }, []);

  const login = useCallback(async (credentials: LoginCredentials) => {
    setError(null);

    try {
      const tokens = await signIn(credentials);
      const nextSession: AuthSession = {
        accessToken: tokens.access,
        refreshToken: tokens.refresh,
        email: credentials.email,
        passwordExpired: tokens.password_expired,
        mustChangePassword: tokens.must_change_password,
        lastActivityAt: Date.now(),
      };

      setSession(nextSession);
      return nextSession;
    } catch (cause) {
      clearSession();
      setError(getSignInErrorMessage(cause));
      throw cause;
    }
  }, [clearSession, setSession]);

  return useMemo(
    () => ({
      session,
      isAuthenticated: hasAuthSession(session),
      ready,
      error,
      login,
      setSession,
      clearSession,
      setError,
    }),
    [clearSession, error, login, ready, session, setSession],
  );
}
